<template>
  <div class="flex" :class="{ relative: isDealsListView }">
    <button
      ref="menuActivator"
      v-on-click-outside="closeMenu"
      type="button"
      class="flex h-[24px] w-auto grow-0 items-center justify-center rounded-full px-2-5 text-center text-xxs font-bold uppercase"
      :class="{
        'cursor-pointer': canChangeMode,
      }"
      :style="buttonStyles"
      role="modePill"
      data-cy="deal-mode-button"
      @click.stop="toggleMenu"
    >
      <div class="flex space-x-2 whitespace-nowrap">
        <icon-base
          v-if="model?.deal_mode === DEAL_MODE_TEST_KEY"
          :icon="IconSandbox"
        />
        <span>{{ dealModeText }}</span>
        <span v-if="canChangeMode" class="-translate-y-0-5 transform">
          <icon-base
            height="6"
            width="10"
            :icon="IconDropdownArrow"
            :class="{ 'rotate-180 transform': showMenu }"
          />
        </span>
      </div>
    </button>

    <ul
      v-if="canChangeMode && showMenu"
      ref="dropdownMenu"
      class="absolute z-50 h-auto w-max min-w-20 overflow-auto rounded-md bg-white py-2 text-sm text-gray-500 shadow-lg"
    >
      <li
        v-for="(mode, key) in DEAL_MODES"
        :key="key"
        class="cursor-pointer px-3 py-2 text-xs font-semibold whitespace-nowrap uppercase hover:bg-gray-200"
        :data-cy="`listItem-${mode}`"
        @click.stop="handleModeChange(key)"
      >
        <span class="cursor-pointer">{{ mode }}</span>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useStore } from "vuex";

import pick from "lodash/pick";

import { getPillStyle } from "@/helpers/UI";
import {
  COLORS,
  DEAL_MODES,
  DEAL_MODE_COLOR_CODES,
  DEAL_MODE_LIVE,
  DEAL_MODE_TEST_KEY,
  DEAL_MODE_TEXT_COLOR_CODES,
} from "@/helpers/constants";
import { useAuth } from "@/hooks/auth";
import { useTeleportV2 } from "@/hooks/elements";
import { useDropdownMenu } from "@/hooks/menus";

import IconDropdownArrow from "@/components/icons/IconDropdownArrow.vue";
import IconSandbox from "@/components/icons/IconSandbox.vue";

import type { DealMode, IApplication } from "@/models/applications";
import type { PropType } from "vue";

const props = defineProps({
  model: {
    type: Object as PropType<IApplication>,
    default: null,
  },
  value: {
    type: Number as PropType<DealMode>,
    default: null,
  },
  isDealsListView: {
    type: Boolean,
    default: false,
  },
});

const { dispatch } = useStore();
const { isLendflowUser, isClientAdmin, isClientUnderwriter } = useAuth();

const dropdownMenu = ref<HTMLElement | null>(null);
const { menuActivator, showMenu, toggleMenu, closeMenu } = useDropdownMenu();

useTeleportV2({
  target: dropdownMenu,
  to: "#menus",
  offset: { top: 5 },
  checkForOverflow: true,
});

const canChangeMode = computed(
  () => isLendflowUser || isClientAdmin || isClientUnderwriter
);

const dealModeText = computed(() =>
  props.model?.deal_mode ? DEAL_MODES[props.model?.deal_mode] : DEAL_MODE_LIVE
);

const pillStyles = computed(() =>
  getPillStyle({
    color: COLORS[colorIndex.value],
    opaque: props.model?.deal_mode === DEAL_MODE_TEST_KEY,
  })
);

const buttonStyles = computed(() =>
  colorIndex.value !== null
    ? { ...getBackgroundStyle(), ...getTextStyle() }
    : { backgroundColor: "white", color: "#868d9c" }
);

const colorIndex = computed(
  () => DEAL_MODE_COLOR_CODES[dealModeText.value || DEAL_MODE_LIVE]
);

const textColorIndex = computed(
  () => DEAL_MODE_TEXT_COLOR_CODES[dealModeText.value || DEAL_MODE_LIVE]
);

const getBackgroundStyle = () => {
  return pick(pillStyles.value, ["backgroundColor", "borderStyle"]);
};

const getTextStyle = () => {
  return {
    color: `rgb(${COLORS[textColorIndex.value]})`,
  };
};

const handleModeChange = (deal_mode: keyof typeof DEAL_MODES) => {
  dispatch("applications/updateApplicationDealMode", {
    id: props.model.id,
    deal_mode: +deal_mode,
  });
  closeMenu({
    afterClose: () => {
      return;
    },
  });
};
</script>
